import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload, Trash2, ArrowRight, Check, Sun, User, Ban, ImageIcon, Smartphone, BookOpen, AlertCircle } from "lucide-react";
import { api, API, getToken } from "../lib/api";
import { toast } from "sonner";

const MAX_PHOTOS = 10;
const MIN_PHOTOS = 3;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const TIPS = [
  { icon: Sun, text: "Use clear, well-lit photos of your face" },
  { icon: User, text: "Include at least one full-face front-on photo" },
  { icon: Ban, text: "Avoid heavy filters or sunglasses" },
  { icon: ImageIcon, text: "Mix of close-up and half-body shots works best" },
  { icon: Smartphone, text: "Phone selfies are fine — they don't need to be professional" },
];

export default function Step1Upload() {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef();

  const refresh = useCallback(async () => {
    const r = await api.get("/reference-photos");
    setPhotos(r.data.photos || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const validate = (files) => {
    const valid = [];
    for (const f of files) {
      if (!ACCEPT.includes(f.type)) {
        toast.error(`${f.name}: only JPG, PNG, or WEBP allowed`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: file is over 10MB`);
        continue;
      }
      valid.push(f);
    }
    const remaining = MAX_PHOTOS - photos.length;
    if (valid.length > remaining) {
      toast.error(`You can upload ${remaining} more photo${remaining === 1 ? "" : "s"} (max ${MAX_PHOTOS})`);
      return valid.slice(0, remaining);
    }
    return valid;
  };

  const handleFiles = async (filesIn) => {
    const files = validate(Array.from(filesIn || []));
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/reference-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} uploaded`);
      await refresh();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reference photo?")) return;
    await api.delete(`/reference-photos/${id}`);
    refresh();
  };

  const count = photos.length;
  const canContinue = count >= MIN_PHOTOS;
  const atMax = count >= MAX_PHOTOS;

  return (
    <div className="max-w-6xl mx-auto" data-testid="step1-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="hairline" />
        <span className="eyebrow">Step 1 of 6</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D]">Step 1: Upload Your Reference Photos</h1>
      <p className="text-[#52525b] mt-3 text-lg max-w-3xl leading-relaxed">
        These photos teach the AI what you look like. The better your reference photos, the better your results.
      </p>

      <div className="mt-10 grid lg:grid-cols-3 gap-8">
        {/* Uploader + thumbnails */}
        <div className="lg:col-span-2 space-y-6">
          {/* Counter */}
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="font-display text-3xl text-[#1A1F3D]" data-testid="upload-counter">
                {count} <span className="text-[#52525b] text-2xl">of {MAX_PHOTOS}</span>
              </div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[#52525b] mt-1">photos uploaded</div>
            </div>
            <div className="flex-1 max-w-xs">
              <div className="h-2 rounded-full bg-[#F5E6E0] overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${canContinue ? "bg-[#D4A853]" : "bg-[#1A1F3D]/60"}`}
                  style={{ width: `${Math.min(100, (count / MAX_PHOTOS) * 100)}%` }}
                />
              </div>
              <div className="text-xs text-[#52525b] mt-1">
                {canContinue ? "You're ready for Step 2" : `${Math.max(0, MIN_PHOTOS - count)} more to unlock Step 2`}
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <div
            onClick={() => !atMax && fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!atMax) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`relative bsp-card p-10 md:p-14 text-center cursor-pointer transition-all duration-200 ${
              atMax ? "opacity-60 cursor-not-allowed" : ""
            } ${dragOver ? "ring-2 ring-[#D4A853] bg-[#F5E6E0]/40" : "hover:border-[#D4A853]"}`}
            style={{ border: dragOver ? "2px dashed #D4A853" : "2px dashed #e5e7eb" }}
            data-testid="drop-zone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="file-input"
            />
            <div className="w-16 h-16 rounded-2xl bg-[#1A1F3D] flex items-center justify-center mx-auto">
              <Upload className="w-7 h-7 text-[#D4A853]" strokeWidth={1.6} />
            </div>
            <h3 className="font-display text-2xl text-[#1A1F3D] mt-5">
              {atMax ? "You've reached the 10 photo limit" : uploading ? "Uploading…" : "Drag photos here, or click to browse"}
            </h3>
            <p className="mt-2 text-sm text-[#52525b]">JPG, PNG, or WEBP · Max {MAX_PHOTOS} photos</p>
            {!atMax && (
              <span className="btn-primary mt-6 inline-flex pointer-events-none" aria-hidden>
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Choose photos"}
              </span>
            )}
          </div>

          {/* Thumbnails */}
          {loading ? (
            <div className="text-[#52525b] text-sm">Loading…</div>
          ) : photos.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="thumb-grid">
              {photos.map((p) => (
                <Thumb key={p.id} photo={p} onRemove={() => remove(p.id)} />
              ))}
            </div>
          ) : (
            <div className="bsp-card p-6 flex items-center gap-3 text-sm text-[#52525b]" data-testid="empty-hint">
              <AlertCircle className="w-4 h-4 text-[#D4A853]" />
              No reference photos yet. Upload a few clear photos of yourself to get started — at least {MIN_PHOTOS} to unlock Step 2.
            </div>
          )}

          {/* Continue */}
          {canContinue && (
            <div className="bsp-card p-6 flex items-center justify-between gap-4 flex-wrap bg-[#1A1F3D] text-white" data-testid="continue-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4A853] flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#1A1F3D]" strokeWidth={2.4} />
                </div>
                <div>
                  <div className="font-display text-xl">Step 1 complete.</div>
                  <div className="text-white/70 text-sm">Time to set up your brand profile.</div>
                </div>
              </div>
              <button
                onClick={() => navigate("/app/step2")}
                className="px-5 py-3 rounded-lg bg-[#D4A853] text-[#1A1F3D] font-semibold inline-flex items-center gap-2 transition-colors hover:bg-[#C29543]"
                data-testid="continue-step2"
              >
                Continue to Step 2 <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Tips panel */}
        <aside className="lg:col-span-1">
          <div className="bsp-card p-6 lg:sticky lg:top-6" data-testid="tips-card">
            <span className="eyebrow">Tips for great results</span>
            <h3 className="font-display text-2xl text-[#1A1F3D] mt-2">What to upload</h3>
            <ul className="mt-5 space-y-4">
              {TIPS.map((t, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`tip-${i}`}>
                  <span className="mt-0.5 w-8 h-8 rounded-full bg-[#F5E6E0] flex items-center justify-center shrink-0">
                    <t.icon className="w-4 h-4 text-[#1A1F3D]" strokeWidth={1.7} />
                  </span>
                  <span className="text-sm text-[#2D2D2D] leading-snug pt-1">{t.text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-[#e5e7eb]">
              <Link
                to="/app/resources"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#1A1F3D] hover:text-[#D4A853] transition-colors"
                data-testid="prep-guide-link"
              >
                <BookOpen className="w-4 h-4" />
                Read the full Reference Photo Prep Guide
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Thumb = ({ photo, onRemove }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/reference-photos/${photo.id}/file?token=${encodeURIComponent(getToken() || "")}`);
        if (!r.ok) return;
        const b = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setSrc(url);
      } catch (err) {
        console.error("Failed to load reference photo thumbnail:", err);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo.id]);

  return (
    <div className="relative group aspect-square rounded-lg overflow-hidden bg-[#F5E6E0] bsp-card-hover" data-testid={`thumb-${photo.id}`}>
      {src && <img src={src} alt={photo.file_name} className="w-full h-full object-cover" />}
      <button
        onClick={onRemove}
        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex items-center justify-center shadow"
        aria-label="Delete photo"
        data-testid={`thumb-delete-${photo.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
